import { influencer } from '@round/api';
import { Checkbox, Skeleton, tableHelpers } from '@round/creatorbase-ui';
import { CellContext, getCreatorName } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { HeaderContext, getTableMeta } from '../../helpers';
import styles from './SelectCreatorCell.module.css';

type Props = CellContext & {
    onToggle: (creator: influencer.CreatorSearchResult) => void;
};

export const SelectCreatorCell = ({ row: { original }, table, onToggle }: Props) => {
    const { isLoading, getIsCreatorSelected, getIsCreatorSelectable } = getTableMeta(table);

    if (tableHelpers.isLoadingRow(original) || isLoading) {
        return <Skeleton className={styles.skeleton} />;
    }

    // Nothing left to add means nothing to select, so those rows carry no checkbox at all.
    if (!getIsCreatorSelectable(original)) {
        return null;
    }

    return (
        <Checkbox
            checked={getIsCreatorSelected(original)}
            onCheckedChange={() => onToggle(original)}
            aria-label={`Select ${getCreatorName(original)}`}
        />
    );
};

type SelectAllHeaderProps = HeaderContext & {
    onSelectAll: (creators: influencer.CreatorSearchResult[]) => void;
    onDeselectAll: (creators: influencer.CreatorSearchResult[]) => void;
};

export const SelectAllCreatorsHeader = ({ table, onSelectAll, onDeselectAll }: SelectAllHeaderProps) => {
    const { getIsCreatorSelected, getIsCreatorSelectable } = getTableMeta(table);

    const selectableCreators = table.getRowModel().rows.flatMap(({ original }) => {
        if (tableHelpers.isLoadingRow(original) || !getIsCreatorSelectable(original)) {
            return [];
        }

        return [original];
    });

    // Scoped to the rows loaded under the current search and filters, so it reaches what the user can
    // see rather than a selection that spans every view they have been through.
    const areAllSelected = selectableCreators.length > 0 && selectableCreators.every(getIsCreatorSelected);
    const areSomeSelected = selectableCreators.some(getIsCreatorSelected);

    return (
        <Checkbox
            checked={areAllSelected}
            indeterminate={!areAllSelected && areSomeSelected}
            onCheckedChange={() =>
                areAllSelected ? onDeselectAll(selectableCreators) : onSelectAll(selectableCreators)
            }
            disabled={!selectableCreators.length}
            aria-label="Select all creators"
        />
    );
};
